import { createContext, useContext, type ReactNode } from 'react';
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

// Context to share isMobile decision from parent to children,
// preventing mismatched Radix context during viewport changes.
const ResponsiveContext = createContext<boolean>(false);

interface ResponsivePopoverProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

interface ResponsivePopoverContentProps {
  className?: string;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
}

interface ResponsivePopoverTriggerProps {
  children: ReactNode;
  asChild?: boolean;
}

function ResponsivePopover({ open, onOpenChange, children }: ResponsivePopoverProps) {
  const isMobile = useIsMobile();

  return (
    <ResponsiveContext.Provider value={isMobile}>
      {isMobile ? (
        <Drawer open={open} onOpenChange={onOpenChange}>{children}</Drawer>
      ) : (
        <Popover open={open} onOpenChange={onOpenChange} modal={true}>{children}</Popover>
      )}
    </ResponsiveContext.Provider>
  );
}

function ResponsivePopoverTrigger({ children, asChild }: ResponsivePopoverTriggerProps) {
  const isMobile = useContext(ResponsiveContext);

  if (isMobile) {
    return <DrawerTrigger asChild={asChild}>{children}</DrawerTrigger>;
  }

  return <PopoverTrigger asChild={asChild}>{children}</PopoverTrigger>;
}

function ResponsivePopoverContent({
  className,
  children,
  align = 'start',
}: ResponsivePopoverContentProps) {
  const isMobile = useContext(ResponsiveContext);

  if (isMobile) {
    return (
      <DrawerContent>
        {/* Visually hidden title for screen readers */}
        <DrawerTitle className="sr-only">Select an option</DrawerTitle>
        <div className="max-h-[70vh] overflow-y-auto px-2 pb-6 pt-2">{children}</div>
      </DrawerContent>
    );
  }

  return (
    <PopoverContent className={cn('w-[400px]', className)} align={align}>
      {children}
    </PopoverContent>
  );
}

export { ResponsivePopover, ResponsivePopoverContent, ResponsivePopoverTrigger };
